import { useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useInstallPrompt } from '../hooks/useInstallPrompt';

interface Props {
  className?: string;
  /** Hide the button entirely if there's nothing to do (already installed, or no prompt available on a non-iOS browser). Defaults to true. */
  autoHide?: boolean;
  /** Label override. */
  label?: string;
}

export function InstallButton({ className, autoHide = true, label = 'Add to Home Screen' }: Props) {
  const { canInstall, isIos, installed, install } = useInstallPrompt();
  const [showIos, setShowIos] = useState(false);

  if (installed) return null;
  if (!canInstall && !isIos && autoHide) return null;

  return (
    <>
      <button
        type="button"
        className={className || 'btn-primary'}
        onClick={() => {
          if (canInstall) install();
          else if (isIos) setShowIos(true);
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M12 3v12" />
          <path d="M7 8l5-5 5 5" />
          <path d="M5 21h14" />
        </svg>
        {label}
      </button>
      <AnimatePresence>
        {showIos && (
          <IosInstructions onClose={() => setShowIos(false)} />
        )}
      </AnimatePresence>
    </>
  );
}

function IosInstructions({ onClose }: { onClose: () => void }) {
  // Portal to <body>: this modal is rendered inside the sticky <header>, whose
  // `backdrop-blur` (backdrop-filter) establishes a containing block for
  // fixed-position descendants — without the portal, `fixed inset-0` anchors to
  // the thin header strip and the modal renders off-screen.
  //
  // Size to the *dynamic* viewport (100dvh), not `inset-0` (the layout
  // viewport). On iOS the layout viewport is taller than the visible area, so a
  // bottom-anchored sheet (`items-end`) inside `inset-0` lands behind the
  // browser's bottom toolbar — off the visible screen. Bottom-padding clears the
  // home indicator / toolbar.
  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-x-0 top-0 z-50 h-[100dvh] bg-ink-950/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 pb-[max(env(safe-area-inset-bottom),1rem)]"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 30, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 280, damping: 26 }}
        className="panel p-6 w-full max-w-sm space-y-3"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="font-display text-lg font-bold">Install Durak on your iPhone</h3>
        <ol className="text-sm text-ink-100 space-y-2 list-decimal list-inside">
          <li>
            Tap the <span className="font-semibold">Share</span> icon{' '}
            <svg className="inline -mt-0.5" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M12 3v12" />
              <path d="M7 8l5-5 5 5" />
              <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
            </svg>{' '}
            at the bottom of Safari.
          </li>
          <li>Scroll down and tap <span className="font-semibold">Add to Home Screen</span>.</li>
          <li>Tap <span className="font-semibold">Add</span>. The Durak icon will appear on your home screen.</li>
        </ol>
        <p className="text-xs text-ink-300">
          This only works in Safari. If you opened the site in Chrome or another browser, paste the URL into Safari first.
        </p>
        <div className="flex justify-end pt-1">
          <button className="btn-ghost" onClick={onClose}>Got it</button>
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  );
}
